import type { VenueSectionData } from './SeatMap.types'

export type ColiseoArea = 'pref-norte' | 'pref-sur' | 'cancha-n' | 'cancha-o' | 'cancha-e'

export interface ColiseoZone {
  area: ColiseoArea
  orientation: 'horizontal' | 'vertical'
}

/**
 * Infiere la zona del coliseo de una sección a partir de name + subSection.
 * name contiene PREFERENCIA o CANCHA; subSection (o name) contiene el lado.
 * Devuelve null si la sección no pertenece al layout de coliseo.
 */
export function resolveColiseoZone(section: VenueSectionData): ColiseoZone | null {
  const full = `${section.name} ${section.subSection ?? ''}`.toUpperCase()

  const isNorte = /\b(NORTE|ALTA|ARRIBA)\b/.test(full)
  const isOeste = /\b(OESTE|IZQ|IZQUIERDA|LEFT)\b/.test(full)
  const isEste = /\b(ESTE|DER|DERECHA|RIGHT)\b/.test(full)

  const isCancha = full.includes('CANCHA')
  const isPref = full.includes('PREFERENCIA') || full.includes('PREFERENCIAL')

  if (isCancha) {
    if (isOeste) return { area: 'cancha-o', orientation: 'vertical' }
    if (isEste) return { area: 'cancha-e', orientation: 'vertical' }
    // CANCHA sin lado o con NORTE → franja horizontal superior
    return { area: 'cancha-n', orientation: 'horizontal' }
  }
  if (isPref) {
    if (isNorte) return { area: 'pref-norte', orientation: 'horizontal' }
    // PREFERENCIA sin lado o con SUR → gradería inferior
    return { area: 'pref-sur', orientation: 'horizontal' }
  }
  return null
}

const SIDE_RE = /\b(NORTE|SUR|ALTA|BAJA|ARRIBA|ABAJO|OESTE|ESTE|IZQ|IZQUIERDA|DER|DERECHA|LEFT|RIGHT)\b/

function hasExplicitSide(section: VenueSectionData): boolean {
  return SIDE_RE.test(`${section.name} ${section.subSection ?? ''}`.toUpperCase())
}

/** True si al menos una sección mapea a una zona del coliseo Y tiene un lado explícito
 *  (evita falsos positivos con venues genéricos que solo tengan una localidad "Preferencia"/"Cancha"). */
export function looksLikeColiseo(sections: VenueSectionData[]): boolean {
  return sections.some((s) => resolveColiseoZone(s) !== null && hasExplicitSide(s))
}
